import React, { memo } from "react";
import { keyframes } from "@emotion/react";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Text } from "@UI/Components/Text/Text";
import { Bell } from "@UI/Components/Icons/Bell";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";

/** Pendulum swing during the first ~16% of a 5s cycle, then rest. */
const bellRing = keyframes`
  0% { transform: rotate(0); }
  4% { transform: rotate(16deg); }
  8% { transform: rotate(-12deg); }
  12% { transform: rotate(6deg); }
  16%, 100% { transform: rotate(0); }
`;

interface UnreadMessagesBannerProps {
  onSeeMore: () => void;
}

/** Full-width notice that unread messages exist; "See more" is handled by the parent. */
export const UnreadMessagesBanner = memo(
  ({ onSeeMore }: UnreadMessagesBannerProps) => (
    <Flex
      w="full"
      bg="red.50"
      borderRadius="10px"
      px={4}
      py={3}
      alignItems="center"
      justifyContent="space-between"
      gap={3}
    >
      <Flex alignItems="center" gap={3}>
        <Icon
          as={Bell}
          color="gray.600"
          flexShrink={0}
          transformOrigin="top center"
          animation={`${bellRing} 3s ease-in-out infinite`}
          sx={{
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        <Text m="0" color="gray.600">
          <FormattedMessage
            {...messages["serviceRequest.candidates.unreadBanner.text"]}
            values={{
              bold: (...chunks) => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      </Flex>
      <Text
        as="button"
        type="button"
        m="0"
        flexShrink={0}
        color="pink.500"
        textDecoration="underline"
        onClick={onSeeMore}
      >
        <FormattedMessage
          {...messages["serviceRequest.candidates.unreadBanner.seeMore"]}
        />
      </Text>
    </Flex>
  )
);

UnreadMessagesBanner.displayName = "UnreadMessagesBanner";
